import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCounselorOptions } from '../../hooks/useCounselorOptions';

interface BulkAssignDialogProps {
  open: boolean;
  leadCount: number;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (userId: number, reason?: string) => Promise<void>;
}

export const BulkAssignDialog = ({ open, leadCount, errorMessage, onClose, onSubmit }: BulkAssignDialogProps) => {
  const [userId, setUserId] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { counselors } = useCounselorOptions(open);

  const handleSubmit = async () => {
    if (!userId) return;
    setSubmitting(true);
    try {
      await onSubmit(userId, reason || undefined);
      setReason('');
      setUserId('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Assign {leadCount} lead(s)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Choose a counselor to assign the selected leads to.
          </Typography>
          <TextField
            select
            label="Assign to counselor"
            fullWidth
            value={userId}
            onChange={(e) => setUserId(Number(e.target.value))}
          >
            {counselors.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.firstName} {u.lastName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Reason (optional)"
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!userId || submitting} onClick={handleSubmit}>
          {submitting ? 'Assigning…' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
